import {
  AcoFormData,
  AdgroupFormData,
  CatalogWordListItem,
  CREATIVE_TYPE,
  FullNormalFormData,
  PrecheckResult,
  SmartTextSource,
  CreativeEntryCode
} from '@tt4b/ttam-enums';
import { computed, onMounted, Ref, ref, SetupContext, watch } from '@vue/composition-api';
import { get, isEmpty, cloneDeep, debounce } from 'lodash-es';
import { i18n } from './../../../i18n';
import {
  useTriggerSmartText,
  useGetRealTimePrecheckApi,
  useSmartText,
  useStore,
  StateEnum,
  useCreativeFormItemPrecheck,
  NetStateEnum,
  useCreativeActionTypeIs
} from '@tt4b/ttam-hooks';
import { getCountryCodeId, getCurrentLang, stringLength } from '@tt4b/ttam-utils';
import { useElementFocusing } from './../../../hooks/creation/creative/useElementFocusing/index';
import {
  typeTemplateTypoModel,
  typeTypoModel,
  DPA_WORD_PATTERN_LEFT as patternLeft,
  DPA_WORD_PATTERN_RIGHT as patternRight,
  realTimePrecheckSplicingMap,
  checkType
} from './types';
import { trackEvent } from './../../../utils/index';

function getFakeAdId(options: { adgroupData: AdgroupFormData }) {
  const { adgroupData } = options;
  return adgroupData?.adId || (adgroupData as any)?.fakeAdIds?.join?.('');
}

export const MAX_TITLE_LENGTH = 100;

export function useCatalogTransformTitle() {
  // 后端返回标题的动态词包格式和前端不同，需要反向转换一次
  // {$品类$}普通文案 => [品类]普通文案
  const transformTitle = (title = '', words) => {
    const pattern = /\{\$([^{$}]+)\$\}/;
    if (!pattern.test(title)) {
      return title;
    }
    return title.replace(/\{\$([^{$}]+)\$\}/g, (match, wordText) => {
      if ((words || []).find(item => item.name === wordText)) {
        return `${patternLeft}${wordText}${patternRight}`;
      }
      return match;
    });
  };
  return {
    transformTitle,
  };
}
/**
 * 动态词包相关handler
 */
// eslint-disable-next-line max-lines-per-function
export function useAdCatalogTextHandler(opt: {
  title: Ref<string>;
  textareaEle: Ref<HTMLInputElement>; // textarea DOM
  catalogTitle: Ref<HTMLFormElement>; // formItem DOM
}) {
  const { state } = useStore();
  const catalogWordList = computed(() => state[StateEnum.catalogLexiconList].data.value);
  const { title, textareaEle, catalogTitle } = opt;
  const cursorIndex = ref(0);
  const findWordFromList = (text: string) => catalogWordList.value.find(item => item.name === text);
  // 匹配广告文案中所有的动态词包
  const wordPattern = new RegExp(`\\${patternLeft}[^${patternLeft}\\${patternRight}]+\\${patternRight}`, 'g');
  const wordsPos = computed(() => {
    const pos = [];
    let curPosTotal = 0;
    let curTitle = title.value;
    wordPattern.lastIndex = 0;
    let regWord = wordPattern.exec(curTitle);
    while (regWord) {
      if (findWordFromList(regWord[0].slice(1, -1))) {
        pos.push([curPosTotal + regWord.index + 1, curPosTotal + regWord.index + regWord[0].length]);
      }
      curPosTotal += regWord.index + regWord[0].length;
      curTitle = curTitle.slice(regWord.index + regWord[0].length);
      wordPattern.lastIndex = 0;
      regWord = wordPattern.exec(curTitle);
    }
    return pos;
  });

  // 设置元素光标
  const setElementCursor = (el, index) => {
    if (el.setSelectionRange) {
      // IE Support
      el.focus();
      el.setSelectionRange(index, index);
    } else if (el.createTextRange) {
      // Firefox support
      const range = el.createTextRange();
      range.collapse(true);
      range.moveEnd('character', index);
      range.moveStart('character', index);
      range.select();
    }
  };
  // 判断区间内是否与词包产生交集
  const ifCursorMixWord = (start: number, end: number) =>
    Boolean(
      wordsPos.value.find(
        ([left, right]) =>
          (start >= left && start < right) || (end >= left && end < right) || (start < left && end >= right)
      )
    );
  // 调整输入框光标
  const adjustCursor = () => {
    const { selectionStart: start, selectionEnd: end } = textareaEle.value;
    const isCursorMixWord = ifCursorMixWord(start, end);
    if (isCursorMixWord && end === start) {
      // 光标点击词包内
      const curPos = Number(start) + title.value.slice(start).match(new RegExp(`\\${patternRight}`)).index + 1;
      setElementCursor(textareaEle.value, curPos);
      cursorIndex.value = curPos;
    } else if (isCursorMixWord && end !== start) {
      // 光标选择词包的部分字符，或者包括了部分词包字符，或者包括了所有词包
      setElementCursor(textareaEle.value, title.value.length);
      cursorIndex.value = title.value.length;
    } else {
      cursorIndex.value = start;
    }
  };
  // 处理回退键、左右移动键
  const handleKeydown = e => {
    // 使用delete进行前删除操作
    if (e.keyCode === 8) {
      const whichWord = wordsPos.value.find(pos => pos[1] === cursorIndex.value);
      // 删除动态词包
      if (whichWord) {
        const wordStart = whichWord[0];
        const wordEnd = whichWord[1];
        title.value = title.value.slice(0, wordStart - 1) + title.value.slice(wordEnd);
        cursorIndex.value = title.value.length;
        // 调试发现这句不起作用，光标总是会跳到末尾，导致诡异的bug，例如再次向左移动时光标位置不对。
        // 所以暂时将光标直接设置到末尾
        // this.cursorIndex = Math.max(this.cursorIndex - (wordEnd - wordStart) - 1, 0);
        setElementCursor(e.target, cursorIndex.value);
        e.preventDefault();
      } else {
        // 删除普通文本
        cursorIndex.value = textareaEle.value.selectionStart;
      }
    }
    // 光标左右移动
    if (e.keyCode === 37 || e.keyCode === 39) {
      cursorIndex.value = e.keyCode === 37 ? cursorIndex.value - 1 : cursorIndex.value + 1;
      // 移动到了词包内部
      if (ifCursorMixWord(cursorIndex.value, cursorIndex.value)) {
        const whichWord = wordsPos.value.find(
          ([left, right]) => cursorIndex.value >= left && cursorIndex.value < right
        );
        const stepWidth = whichWord[1] - whichWord[0]; // 计算词包步长宽度
        cursorIndex.value =
          e.keyCode === 37 ?
            Math.max(cursorIndex.value - stepWidth, 0) :
            Math.min(cursorIndex.value + stepWidth, title.value.length);
        setElementCursor(e.target, cursorIndex.value);
        e.preventDefault();
      }
    }
  };
  const handleInput = newVal => {
    title.value = newVal;
    cursorIndex.value = textareaEle.value.selectionStart;
  };
  // 增加动态词包
  const addWord = (word: CatalogWordListItem) => {
    if (!word.is_available) {
      return;
    }
    const prefix = title.value.slice(0, cursorIndex.value);
    const suffix = title.value.slice(cursorIndex.value);
    const currentTitle = `${prefix}${patternLeft}${word.name}${patternRight}${suffix}`;
    title.value = currentTitle; // 将词包插入到标题中
    cursorIndex.value = currentTitle.length; // 插入词包后，光标位置记录为标题末尾
    // 选择词包后，触发表单项校验，如果校验通过则清除校验报错信息
    catalogTitle.value.validate('blur');
  };
  // 标题中用到的词包列表， 是validWords的子集
  const wordList = computed(() => {
    wordPattern.lastIndex = 0;
    return (title.value.match(wordPattern) || [])
      .map(wordWrapper => wordWrapper.slice(1, -1))
      .reduce((result, text) => {
        const word = findWordFromList(text);
        return result.concat(word || []);
      }, []);
  });
  // 将标题中的词包进行自定义转换
  const transformInputValue = (replace = (str: string) => '') => {
    let transformedTitle = title.value || '';
    wordList.value.forEach(word => {
      transformedTitle = transformedTitle.replace(`${patternLeft}${word.name}${patternRight}`, replace(word.name));
    });
    return transformedTitle;
  };
  const plainTitle = computed(() => transformInputValue(() => '').trim());

  // 后端接口需要，使用到的动态词包id
  const wordIdList = computed(() => wordList.value.map(word => word.lexiconID));
  return {
    wordPattern,
    wordsPos,
    plainTitle,
    wordList,
    wordIdList,
    setElementCursor,
    transformInputValue,
    ifCursorMixWord,
    adjustCursor,
    handleKeydown,
    handleInput,
    addWord,
  };
}
// import { trackEvent } from '@/track';
export function useCatalogWordListDataSource(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const params = computed(() => ({
    platformId: opt.adgroupData.value.productPlatformId,
    bc_id: opt.adgroupData.value.catalogAuthorizedBc,
  }));
  const { getStore } = useStore();
  const { data: catalogWordList, run: getCatalogWordList } = getStore(
    NetStateEnum.catalogLexiconList,
    params.value,
    true
  );
  return {
    catalogWordList,
    getCatalogWordList,
  };
}

const hasLoadCatalogWordList = ref(false); // 是否请求过一次的标志，避免字段校验的时候接口权限错误重复请求

export function useGetCatalogTextValidatorParams(opt: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
}) {
  const { creativeData, adgroupData } = opt;
  const { state } = useStore();
  const { transformTitle } = useCatalogTransformTitle();
  const wordPattern = new RegExp(`\\${patternLeft}[^${patternLeft}\\${patternRight}]+\\${patternRight}`, 'g');
  let catalogWordList;
  const stateCatalogWordList = computed(() => state[StateEnum.catalogLexiconList].data.value);
  // 校验的时候catalogLexiconList内不一定有值，有值了的话就不需要再请求了
  if (stateCatalogWordList.value.length > 0) {
    catalogWordList = stateCatalogWordList;
  } else if (!hasLoadCatalogWordList.value) {
    const result = useCatalogWordListDataSource({ adgroupData });
    hasLoadCatalogWordList.value = true;
    catalogWordList = result?.catalogWordList;
  } else {
    catalogWordList = stateCatalogWordList;
  }
  const findWordFromList = (text: string) => catalogWordList.value.find(item => item.name === text);

  const title = computed(() => transformTitle(creativeData.value.title || '', catalogWordList.value));
  const wordList = computed(() => {
    wordPattern.lastIndex = 0;
    return (title.value.match(wordPattern) || [])
      .map(wordWrapper => wordWrapper.slice(1, -1))
      .reduce((result, text) => {
        const word = findWordFromList(text);
        return result.concat(word || []);
      }, []);
  });
  // 将标题中的词包进行自定义转换
  const transformInputValue = (replace = (str: string) => '') => {
    let transformedTitle = title.value || '';
    wordList.value.forEach(word => {
      transformedTitle = transformedTitle.replace(`${patternLeft}${word.name}${patternRight}`, replace(word.name));
    });
    return transformedTitle;
  };
  const plainTitle = computed(() => transformInputValue(() => '').trim());
  return {
    title,
    wordList,
    plainTitle,
  };
}

export function useTextareaPropsDisabled(opt: {
  isDisabledCreative: Ref<boolean>;
  isPullTTPostNativeAd: Ref<boolean>;
  isTTBAPushVideoNativeAd: Ref<boolean>;
}) {
  const { isDisabledCreative, isPullTTPostNativeAd, isTTBAPushVideoNativeAd } = opt;
  const textareaDisabled = computed(
    () => (isPullTTPostNativeAd.value && !isTTBAPushVideoNativeAd.value) || isDisabledCreative.value
  );
  return {
    textareaDisabled,
  };
}

export function usePopoverGuideId() {
  const { state } = useStore();
  const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);
  const popoverGuideId = `smartTextToolsNotAcoPopoverKey${aadvid.value}`;
  return {
    popoverGuideId,
  };
}

export function useInputNodeFocus() {
  const inputNode = ref();
  onMounted(() => {
    if (inputNode.value?.$children?.[0]) {
      const { setElement } = useElementFocusing();
      setElement(inputNode.value.$children[0].$el);
    }
  });
  return {
    inputNode,
  };
}

export function useTitleValidateStatus() {
  const refTitle = ref();
  const isValidateSuccess = computed(() => !refTitle.value?.validateMessage);
  return {
    refTitle,
    isValidateSuccess,
  };
}

export function useCreativeSmartDrawerHandler() {
  // 智能建议+智能文案+智能翻译
  const { show } = useSmartText();
  const handleOpenSmartText = source => {
    show({ source });
  };
  const handleOpenSmartTranslation = () => {
    show({ isTranslation: true, source: SmartTextSource.TRANSLATION });
  };
  return {
    handleOpenSmartText,
    handleOpenSmartTranslation,
  };
}

export function useTextareaTypoHandler(opt: {
  domId: Ref<string>;
  titleValue: Ref<string>;
  typoModel: Ref<typeTypoModel[]>;
  maxLength: Ref<number>;
}) {
  const { domId, titleValue, typoModel, maxLength } = opt;
  const divDom = ref<HTMLElement | null>(document.createElement('div')); // create dom
  const textAreaLineHeight = ref(''); // textArea中的lineHeight
  const xssTest = title => /<|>/.test(title);
  const templateTypoModel = ref<typeTemplateTypoModel[]>([]); // 计算后的错别字

  const replaceStartTag = '<span>';
  const replaceEndTag = '</span>';
  const replaceTagName = 'span';
  const createDivStyle = {
    position: 'fixed',
    top: '-500px',
    opacity: '0',
  };
  const cssCopy = (dom, textAreaStyle) => {
    const keys = Object.keys(dom.style);
    keys.forEach(item => {
      dom.style[item] = textAreaStyle[item];
    });
  };
  const computedStringLength = computed(() => stringLength(titleValue.value));

  const disableTextSpellCheck = () => {
    (document.getElementById(domId.value) as HTMLElement).setAttribute('spellcheck', 'false');
  };

  const initTextWrap = () => {
    try {
      const textAreaDom = document.getElementById(domId.value) as HTMLElement;
      if (!textAreaDom) {
        return;
      }
      const textAreaStyle = window.getComputedStyle(textAreaDom);
      textAreaLineHeight.value = textAreaStyle.lineHeight;
      // copy textArea的css，使div的文字排版和textArea的文字排版一致
      cssCopy(divDom.value, textAreaStyle);
      // 将插入body的元素移到屏幕之外
      if (!divDom.value) {
        return;
      }
      divDom.value.style.position = createDivStyle.position;
      divDom.value.style.top = createDivStyle.top;
      divDom.value.style.opacity = createDivStyle.opacity;
      document.body.append(divDom.value);
      // 禁用text中的拼写检查
      disableTextSpellCheck();
    } catch (e) {
      console.log(e);
      divDom.value = null;
      textAreaLineHeight.value = '0px';
    }
  };

  const dealReplace = (text, startIndex, endIndex, replaceText) =>
    `${text.slice(0, startIndex)}${replaceText}${text.slice(endIndex)}`;

  const replaceTypoWithSpanTag = value => {
    let title = value;
    // 使用tag包裹错别字
    const deepCloneTypoModel = cloneDeep(typoModel.value);
    deepCloneTypoModel.forEach((item, index) => {
      if (index !== 0) {
        item.startIndex += (replaceStartTag.length + replaceEndTag.length) * index;
        item.endIndex += (replaceStartTag.length + replaceEndTag.length) * index;
      }
      title = dealReplace(
        title,
        item.startIndex,
        item.endIndex,
        `${replaceStartTag}${title.slice(item.startIndex, item.endIndex)}${replaceEndTag}`
      );
    });
    return title;
  };
  /*
   * 错别字模型识别
   * 表现：在文本框的文字下面增加横线，hover会提示正确的文案, 然后根据位置替换
   */
  const getTypoPosition = () => {
    try {
      if (xssTest(titleValue.value)) {
        return;
      }
      if (computedStringLength.value > maxLength.value) {
        throw new Error('max length');
      }
      if (!divDom.value) {
        return;
      }
      divDom.value.innerHTML = replaceTypoWithSpanTag(titleValue.value);
      const itemList = divDom.value.getElementsByTagName(replaceTagName);
      templateTypoModel.value = Array.from(itemList).map(item => ({
        width: item.getClientRects()[0].width,
        top: item.offsetTop,
        left: item.offsetLeft,
      }));
    } catch (e) {
      console.log('templateTypoModel', e);
      templateTypoModel.value = [];
    }
  };
  return {
    divDom,
    textAreaLineHeight,
    templateTypoModel,
    computedStringLength,
    initTextWrap,
    dealReplace,
    getTypoPosition,
  };
}

export const useLanguageMisMatch = (opt: {
  precheckResult: Ref<PrecheckResult>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  activeCreativeIndex?: Ref<number>;
}) => {
  const { precheckResult, creativeData, activeCreativeIndex = ref(0) } = opt;
  // 获取所有的countryHit字符串
  const getCountryHitString = (key, verifyItem) => {
    const modelItem = realTimePrecheckSplicingMap[key];
    const { modelType } = modelItem;
    let result = '';
    for (let i = 0; i < modelType.length; i++) {
      const tryGetCountryHitString = get(verifyItem, `${modelType[i]}[0].details.${modelItem.countryHitCode}`, '');
      if (tryGetCountryHitString) {
        result = tryGetCountryHitString;
      }
    }
    result = JSON.parse(result).hit_countries;
    if (!realTimePrecheckSplicingMap[key].hitcountryisObject) {
      result = JSON.parse(result);
    }
    return result;
  };
  // 获取各自的suggestion
  const getSingleCountriesHitList = key => {
    try {
      const singlePrecheckResult =
        realTimePrecheckSplicingMap[key].checkType === checkType.realTimePrecheck ?
          get(creativeData.value, 'titlesPrecheckResult.materialResults[0]', {}) :
          get(precheckResult.value, `materialResults${activeCreativeIndex.value}`, []);

      const countryhitList = getCountryHitString(key, singlePrecheckResult);
      return Object.values(countryhitList);
    } catch (e) {
      return [];
    }
  };

  // commonDealSuggestion 对suggestion做过滤处理
  const getSuggestion = key => {
    if (realTimePrecheckSplicingMap[key]) {
      return (i18n.t(realTimePrecheckSplicingMap[key].replaceKey) as string).replace(
        realTimePrecheckSplicingMap.material_language_mismatch.occupy,
        getSingleCountriesHitList(key)?.toString()
      );
    }
    return key;
  };

  // suggestion后的链接是否展示
  const isShowSuggestionLink = item => {
    if (realTimePrecheckSplicingMap[item]) {
      return realTimePrecheckSplicingMap[item].link;
    }
    return false;
  };
  const getAllCountriesHitList = key => {
    try {
      const materialResults = get(precheckResult.value, 'materialResults', []);
      const countriesList = [] as Array<any>;
      materialResults.forEach(o => {
        const hitCountries = getCountryHitString(key, o);
        console.log(hitCountries);
        if (isEmpty(hitCountries)) {
          return;
        }
        countriesList.push(...Object.values(hitCountries));
      });
      return [...new Set(countriesList)].toString();
    } catch (e) {
      console.log(e);
      return '';
    }
  };
  const replaceDialogSuggestion = key => {
    const findResult = realTimePrecheckSplicingMap[key];
    if (findResult) {
      return (i18n.t(findResult.replaceKey) as string).replace(
        realTimePrecheckSplicingMap[key].occupy,
        getAllCountriesHitList(key)
      );
    }
    return key;
  };

  return {
    getSingleCountriesHitList,
    getSuggestion,
    isShowSuggestionLink,
    replaceDialogSuggestion,
  };
};

/**
 * 检查title
 * 1. title是否有夸张和侮辱描述
 * 2. title是否有错别字(typo)
 * 3. title语种在广告投放地区是否支持(language mismatch)举个🌰：在美国使用阿拉伯语
 *
 * 目前过滤suggestion做特殊处理的有两个，一个typo一个language mismatch
 * typo完全过滤不用管，language mismatch则是使用replace在页面上进行替换操作。
 */
// eslint-disable-next-line max-lines-per-function
export function useCheckTitle(opt: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  emit: SetupContext['emit'];
}) {
  const { adgroupData, creativeData, emit } = opt;
  const textTypoCode = '21100222'; // 错别字模型Code码
  const ignoreSuggestion = ['material_text_correction']; // title的suggestion忽略的List，有些suggestion是一个key
  const isNative = ref(false);
  const typoModelList = ref([]);
  const suggestionList = ref([]);
  // const { isNative } = useCreativeDataIs();
  const isChecking = ref(false);
  // const { currentLang } = useAccountStore();
  // ---------------- 公共的suggestion过滤--------------------
  // 只过滤外层summaryResults和内层的suggestion;
  const filterSuggestion = data => {
    // 过滤错别字模型，后端返回的建议
    const adsTextsTest = get(data, 'materialResults[0].adsTexts[0]', []);
    adsTextsTest.suggestion = adsTextsTest.suggestion.filter(item => !ignoreSuggestion.includes(item));
    data.summaryResults = data.summaryResults.filter(item => !ignoreSuggestion.includes(item));
  };

  // ----------------suggestion的实时校验处理--------------------
  // title实时校验的的suggestion的操作类型
  const titleSuggestionGlobalOperation = {
    getTitleSuggestionList: () => suggestionList,
    pushTitleSuggestionList: item => {
      suggestionList.value.push(...item);
    },
    setTitleSuggestionList: val => {
      suggestionList.value = val;
    },
  };
  const computedTitleSuggestionList = computed(() => suggestionList.value);

  // ----------------typo的错误处理--------------------
  const computedTypoModel = computed(() => typoModelList.value);
  // 清空错别字模型
  const clearTypoModel = () => {
    if (typoModelList.value.length === 0) {
      return;
    }
    typoModelList.value = [];
  };

  const handleTypo = data => {
    let mapTextTypoList = [];
    try {
      const textTypoList = get(data, `materialResults[0].adsTexts[0].details[${textTypoCode}]`, '[]');
      mapTextTypoList = JSON.parse(textTypoList).map(item => ({
        startIndex: item.word_idx_in_sent,
        endIndex: item.word_idx_in_sent + item.source_word.length,
        correctSpelling: item.predict_word,
      }));
    } catch (e) {
      mapTextTypoList = [];
    }
    typoModelList.value = mapTextTypoList;

    if (mapTextTypoList?.length >= 1) {
      trackEvent('pre_check_real_time_text_typo_work', {
        fake_ad_id: getFakeAdId({
          adgroupData: adgroupData.value,
        }),
        timer: new Date()?.toUTCString(),
        currentLang: getCurrentLang(),
        number: mapTextTypoList.length,
      });
    }
  };

  // ----------------language mismatch相关 封装成了hooks:useLanguageMisMatch--------------------
  // ----------------触发校验相关--------------------

  const handlePrecheckTitle = result => {
    // 首先处理typo，因为suggestion中一个typo的key要送suggestion中过滤出来，不展示在页面上。
    const deepCloneResult = cloneDeep(result);
    // 过滤后端返回的key
    filterSuggestion(deepCloneResult);
    handleTypo(deepCloneResult);
    const suggestion = get(deepCloneResult, 'summaryResults', []);

    if (suggestion?.length >= 1) {
      trackEvent('pre_check_real_time_text', {
        check_type: 'ads_texts',
        fake_ad_id: getFakeAdId({
          adgroupData: adgroupData.value,
        }),
        tag_ids: get(deepCloneResult, 'materialResults[0].adsTexts[0].tagIds', [])?.toString(),
        timer: new Date()?.toUTCString(),
      });
    }

    titleSuggestionGlobalOperation.pushTitleSuggestionList(suggestion);
    emit('creativeData-change', { titlesPrecheckResult: deepCloneResult });
    const { isClickSubmit } = useCreativeFormItemPrecheck();
    isClickSubmit.value = false;
  };

  /**
   * title实时校验
   * @param val 对应的值
   */
  const getCheckTitleInfo = async (val: string) => {
    try {
      isChecking.value = true;
      // 每次校验前，清空上一次的suggestion
      titleSuggestionGlobalOperation.setTitleSuggestionList([]);
      if (!val) {
        return;
      }
      const precheckResult = await useGetRealTimePrecheckApi({
        materials: [{ urls: [], adsTexts: [val] }],
        countries: adgroupData.value.country,
      });
      if (precheckResult) {
        handlePrecheckTitle(precheckResult);
      }
    } catch (error) {
      titleSuggestionGlobalOperation.setTitleSuggestionList([]);
    } finally {
      isChecking.value = false;
    }
  };

  const DEBOUNCE_WAIT_TIME = 300;
  const debounceCheckTitle = debounce(getCheckTitleInfo, DEBOUNCE_WAIT_TIME);
  const title = computed(() => creativeData.value?.title || '');

  const checkTitle = () => {
    if (isNative.value) {
      return;
    }
    if (title.value) {
      debounceCheckTitle(title.value);
    } else {
      // title为空就清空实时的suggestion
      titleSuggestionGlobalOperation.setTitleSuggestionList([]);
    }
  };

  return {
    /** 正在检查中 */
    isChecking,
    /** 是否展示提示信息 */
    computedTitleSuggestionList,
    // typo错别字模型
    computedTypoModel,
    /** 检查原生广告 落地页 是否包含非商业挑战赛标识 */
    checkTitle,
    // 清空typo的方法
    clearTypoModel,
  };
}

/**
 *
 * 上报track
 *
 */
export function useTrackTitleEvent(opt: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
}) {
  const { adgroupData, creativeData } = opt;
  const { state } = useStore();
  const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);
  const isAco = computed(() => adgroupData.value.creativeMaterialMode === CREATIVE_TYPE.PROCEDURAL);
  const triggerParams = computed(() => {
    if (isAco.value) {
      return {
        titles: JSON.stringify(creativeData.value.titleList?.map(item => item.title)),
        country_code: (getCountryCodeId(adgroupData.value.country) || []).join(','),
        ad_id: adgroupData.value.adId,
        external_type: adgroupData.value.externalType,
        source: creativeData.value.source,
        app_name: creativeData.value.appName,
        app_id: adgroupData.value.adRefAppId,
        external_url: creativeData.value.externalUrl,
        objective_type: adgroupData.value.objectiveType,
        portfolio_id: creativeData.value.cardId,
        adv_id: aadvid.value,
        is_aco: isAco.value,
      };
    }
    return {
      video_id: creativeData.value.videoInfo?.vid,
      title: creativeData.value.title,
      country_code: (getCountryCodeId(adgroupData.value.country) || []).join(','),
      ad_id: adgroupData.value.adId,
      external_type: adgroupData.value.externalType,
      source: creativeData.value.source,
      app_name: creativeData.value.appName,
      app_id: adgroupData.value.adRefAppId,
      external_url: creativeData.value.externalUrl,
      objective_type: adgroupData.value.objectiveType,
      portfolio_id: creativeData.value.cardId,
      adv_id: aadvid.value,
      is_aco: isAco.value,
    };
  });
  const videos = computed(() =>
    (creativeData.value.imageList || [])?.filter(item => item.videoInfo).map(item => item.videoInfo)
  );
  const videoIds = computed(() =>
    JSON.stringify(
      (creativeData.value.imageList || [])?.filter(item => item.videoInfo).map(item => item.videoInfo?.vid)
    )
  );
  const attachmentCreativeOptimizeData = computed(() => {
    if (isAco.value) {
      return {
        ...triggerParams.value,
        videos: videos.value,
      };
    }
    return {
      ...triggerParams.value,
    };
  });
  // 避免太过频繁的请求
  const DEBOUNCE_WAIT_TIME = 300;
  const debounceTriggerTitle = debounce(useTriggerSmartText, DEBOUNCE_WAIT_TIME);
  return {
    triggerText: () => debounceTriggerTitle({ ...triggerParams.value, video_ids: isAco.value ? videoIds.value : [] }),
    triggerParams,
    attachmentCreativeOptimizeData,
  };
}

export function useTrackParams(options: { createMode: Ref<string> }) {
  const { createMode } = options;
  const { isEdit, isCopy } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: ref({}) as any,
  });
  const etSmartTextEntryCode = computed(() => {
    if (isEdit.value) {
      return CreativeEntryCode.Edit;
    }
    if (isCopy.value) {
      return CreativeEntryCode.Copy;
    }

    return CreativeEntryCode.Create;
  });

  return {
    etSmartTextEntryCode,
  };
}
